import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Upload, Trash2, FileText, Image as ImageIcon, FileSpreadsheet, File, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp';
const MAX_BYTES = 25 * 1024 * 1024;

const fileIcon = (ext) => {
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return ImageIcon;
  if (['xls', 'xlsx'].includes(ext)) return FileSpreadsheet;
  if (['pdf', 'doc', 'docx'].includes(ext)) return FileText;
  return File;
};
const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// Görev dosya ekleri: PDF, Word, Excel, resim yükle / indir / sil (kalıcı object storage).
export const TaskAttachments = ({ taskId, canManage = true, onCountChange }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/personnel-tasks/${taskId}/attachments`);
      const items = res.data?.items || [];
      setFiles(items);
      if (onCountChange) onCountChange(items.length);
    } catch { /* sessiz */ }
  };
  useEffect(() => { fetchFiles(); /* eslint-disable-next-line */ }, [taskId]);

  const handleSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const f of selected) {
      if (f.size > MAX_BYTES) { toast.error(`${f.name} 25MB'dan büyük`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try {
        await axios.post(`${API}/personnel-tasks/${taskId}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `${f.name} yüklenemedi`);
      }
    }
    if (ok > 0) toast.success(`${ok} dosya yüklendi`);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    fetchFiles();
  };

  const handleDownload = async (file) => {
    try {
      const res = await axios.get(`${API}/task-files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const previewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(file.ext);
      if (previewable) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = file.original_filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Dosya açılamadı'); }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`"${file.original_filename}" silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/task-files/${file.id}`);
      toast.success('Dosya silindi');
      fetchFiles();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Silinemedi'); }
  };

  return (
    <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`task-attachments-${taskId}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
          <Paperclip className="w-3 h-3" /> Ekler ({files.length})
        </span>
        {canManage && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={handleSelect}
              data-testid={`task-file-input-${taskId}`}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
              data-testid={`task-file-upload-btn-${taskId}`}
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? 'Yükleniyor...' : 'Dosya Ekle'}
            </button>
          </>
        )}
      </div>
      {files.length === 0 ? (
        <p className="text-[11px] text-slate-400">Henüz dosya eklenmedi (PDF, Word, Excel, resim)</p>
      ) : (
        <div className="space-y-1">
          {files.map((f) => {
            const Icon = fileIcon(f.ext);
            return (
              <div
                key={f.id}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                data-testid={`task-file-${f.id}`}
              >
                <Icon className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                <button
                  type="button"
                  onClick={() => handleDownload(f)}
                  className="flex-1 min-w-0 text-left truncate text-slate-700 hover:text-indigo-700 hover:underline"
                  data-testid={`task-file-download-${f.id}`}
                  title={f.original_filename}
                >
                  {f.original_filename}
                </button>
                {f.uploaded_by_name && (
                  <span className="text-[10px] text-slate-400 shrink-0 hidden sm:inline">{f.uploaded_by_name}</span>
                )}
                <span className="text-[10px] text-slate-400 shrink-0">{fmtSize(f.size)}</span>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => handleDelete(f)}
                    className="shrink-0 text-rose-400 hover:text-rose-600"
                    data-testid={`task-file-delete-${f.id}`}
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskAttachments;
